import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Button,
  VStack,
  Stack,
  Text,
  Flex,
  Tabs,
  Tab,
  TabList,
  Input,
} from "@chakra-ui/react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks/hooks";
import {
  fetchAllTrips,
  fetchTrips,
} from "../../../store/travelSlice/hikeTripsSlice";
import { HikeTripCard } from "./hikeTripCard";
import { MapPin } from "lucide-react";
import { useJsApiLoader } from "@react-google-maps/api";
import AutocompleteInput from "../../../LocationsModule/PlaceAutocompleteInput";
import MapDisplay from "../../../LocationsModule/MapDisplay";
import { CustomMenu } from "../../CustomReusables/MenuList";
import { CustomDatePicker } from "../../CustomReusables/CustomDatePicker";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";
import { useNavigate } from "react-router-dom";

const fallbackCenter = {
  lat: -13.9626,
  lng: 33.7741,
};

const travelModes = ["DRIVING", "BICYCLING", "TRANSIT"] as const;
const cardsBorderRadii ="8px";
type TravelMode = (typeof travelModes)[number];

const pricePerKm = {
  DRIVING: 5,
  BICYCLING: 2,
  TRANSIT: 10,
};

const toLocalDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const next30Days = Array.from({ length: 30 }, (_, i) => {
  const d = new Date();

  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + i);

  return {
    value: toLocalDateValue(d),
    label: d.toLocaleDateString("en-US", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    }),
  };
});

const getTripDateValue = (trip: any) => {
  return (
    trip?.departDate ||
    trip?.date ||
    trip?.travelDate ||
    trip?.departureDate ||
    ""
  );
};

const normalizeTripDateValue = (trip: any) => {
  const rawValue = getTripDateValue(trip);

  if (!rawValue) return "";

  if (rawValue instanceof Date) {
    return Number.isNaN(rawValue.getTime()) ? "" : toLocalDateValue(rawValue);
  }

  const stringValue = String(rawValue).trim();

  if (!stringValue) return "";

  /*
   * Preserve plain API date values exactly. For timestamps, parse the
   * value into the browser's local date so UTC conversion does not move
   * a Malawi/South Africa trip onto the previous date tab.
   */
  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    return stringValue;
  }

  const parsedDate = new Date(stringValue);

  if (!Number.isNaN(parsedDate.getTime())) {
    return toLocalDateValue(parsedDate);
  }

  const datePrefix = stringValue.match(/^(\d{4}-\d{2}-\d{2})/);

  return datePrefix?.[1] || "";
};

interface HikeSearchTripsProps {
  ownerId?: string;
}

const normalizeOwnerId = (value: any) => {
  if (!value) return "";

  if (typeof value === "object") {
    return String(value.uid || value.id || value._id || "").trim();
  }

  return String(value).trim();
};

const tripBelongsToOwner = (trip: any, ownerId?: string) => {
  const normalizedOwnerId = normalizeOwnerId(ownerId);

  if (!normalizedOwnerId) return true;

  const possibleOwnerIds = [
    trip?.userId,
    trip?.ownerId,
    trip?.ownerUserId,
    trip?.sellerUserId,
    trip?.businessId,
    trip?.createdBy,
    trip?.sellerId,
    trip?.providerId,
    trip?.hostId,
    trip?.driverId,
    trip?.user?.id,
    trip?.user?._id,
    trip?.user?.uid,
    trip?.driver?.id,
    trip?.driver?._id,
    trip?.driver?.uid,
  ];

  return possibleOwnerIds.some(
    (possibleOwnerId) =>
      normalizeOwnerId(possibleOwnerId) === normalizedOwnerId,
  );
};

export const HikeSearchTrips = ({ ownerId }: HikeSearchTripsProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { list, loading, error } = useAppSelector((state) => state.hikeTrips);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [date, setDate] = useState<Date | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedTabDate, setSelectedTabDate] = useState<string | null>(
    next30Days[0].value,
  );
  const [searchResults, setSearchResults] = useState<any[]>([]);

  const [mode, setMode] = useState<TravelMode>("DRIVING");
  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [cost, setCost] = useState<number | null>(null);
  const [mapCenter, setMapCenter] = useState(fallbackCenter);

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const hasManuallySelectedTabRef = useRef(false);

  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";
  const brand = theme.brand;

  const pageText = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;

  const shellBg = theme.gradients?.shellBg || theme.surface;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;

  const panelShadow = theme.shadowMd;
  const cardShadow = theme.shadowSm;

  const searchButtonBg = theme.gradients?.primaryButton || theme.primary;
  const searchButtonHover =
    theme.gradients?.primaryButtonHover || theme.primaryHover;

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });

  const mapsReady =
    Boolean(API_KEY) &&
    isLoaded &&
    !loadError &&
    typeof window !== "undefined" &&
    Boolean(window.google?.maps?.places?.Autocomplete);

  const inputStyles = {
    bg: theme.inputBg,
    color: theme.text,
    border: "1px solid",
    borderColor: theme.inputBorder,
    borderRadius: {cardsBorderRadii},
    fontWeight: "600",
    h: "42px",
    _placeholder: { color: theme.inputPlaceholder },
    _hover: { borderColor: theme.inputBorderFocus },
    _focus: {
      borderColor: theme.inputBorderFocus,
      boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
    },
  };

  const fetchDirections = useCallback(() => {
    if (!from || !to || !mapsReady || !window.google?.maps) return;

    const directionsService = new google.maps.DirectionsService();
    directionsService.route(
      {
        origin: from,
        destination: to,
        travelMode: google.maps.TravelMode[mode],
      },
      (result, status) => {
        if (status === "OK" && result) {
          setDirections(result);
          const leg = result.routes[0].legs[0];
          setDistance(leg.distance?.text || "");
          setDuration(leg.duration?.text || "");
          const distanceInKm = parseFloat(
            leg.distance?.text.replace(/[^0-9.]/g, "") || "0",
          );
          setCost(Number((distanceInKm * (pricePerKm[mode] || 0)).toFixed(2)));
          setMapCenter({
            lat: leg.start_location.lat(),
            lng: leg.start_location.lng(),
          });
        } else {
          console.error("Directions failed:", status);
          setDirections(null);
        }
      },
    );
  }, [from, to, mode, mapsReady]);

  const handleSearch = async () => {
    if (from && to && date) {
      const formattedDate = toLocalDateValue(date);

      try {
        const results = await dispatch(
          fetchTrips({ from, to, date: formattedDate }),
        ).unwrap();

        const ownerScopedResults = Array.isArray(results)
          ? results.filter((trip: any) => tripBelongsToOwner(trip, ownerId))
          : [];

        setSearchResults(ownerScopedResults);
        setHasSearched(true);
        hasManuallySelectedTabRef.current = true;
        setSelectedTabDate(formattedDate);
        fetchDirections();

        setFrom("");
        setTo("");
        setDate(null);
      } catch (err) {
        console.error("Search failed:", err);
        setSearchResults([]);
        setHasSearched(true);
        hasManuallySelectedTabRef.current = true;
        setSelectedTabDate(formattedDate);
      }
    }
  };

  const handleTabClick = (dayValue: string) => {
    hasManuallySelectedTabRef.current = true;
    setSelectedTabDate(dayValue);
    setFrom("");
    setTo("");
    setDate(null);
    setHasSearched(false);
    setSearchResults([]);
    dispatch(fetchAllTrips());
    setDirections(null);
    setDistance("");
    setDuration("");
    setCost(null);
    setMapCenter(fallbackCenter);
  };

  useEffect(() => {
    dispatch(fetchAllTrips());
  }, [dispatch]);

  useEffect(() => {
    hasManuallySelectedTabRef.current = false;
    setFrom("");
    setTo("");
    setDate(null);
    setHasSearched(false);
    setSearchResults([]);
    setSelectedTabDate(next30Days[0].value);
    setDirections(null);
    setDistance("");
    setDuration("");
    setCost(null);
    setMapCenter(fallbackCenter);
  }, [ownerId]);

  useEffect(() => {
    if (selectedTabDate && tabRefs.current[selectedTabDate]) {
      tabRefs.current[selectedTabDate]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selectedTabDate]);

  const allTrips = useMemo(() => {
    const trips = Array.isArray(list) ? list : [];

    return trips.filter((trip: any) => tripBelongsToOwner(trip, ownerId));
  }, [list, ownerId]);

  const availableTripDates = useMemo(() => {
    return new Set(
      allTrips.map((trip: any) => normalizeTripDateValue(trip)).filter(Boolean),
    );
  }, [allTrips]);

  /*
   * On first load, do not leave the user on an empty "today" tab when
   * production has trips on another visible date. A date manually chosen
   * by the user is never changed automatically.
   */
  useEffect(() => {
    if (hasSearched) return;
    if (hasManuallySelectedTabRef.current) return;
    if (!allTrips.length) return;
    if (selectedTabDate && availableTripDates.has(selectedTabDate)) return;

    const firstAvailableDate = next30Days.find((day) =>
      availableTripDates.has(day.value),
    );

    if (firstAvailableDate) {
      setSelectedTabDate(firstAvailableDate.value);
    }
  }, [allTrips, availableTripDates, hasSearched, selectedTabDate]);

  const filteredTrips = hasSearched
    ? searchResults
    : allTrips.filter((trip: any) => {
        if (!selectedTabDate) return true;

        return normalizeTripDateValue(trip) === selectedTabDate;
      });

  const openHikeDetails = (trip: any) => {
    const tripId = trip?._id || trip?.id;

    if (!tripId) return;

    navigate(`/hikes/${tripId}`, {
      state: {
        hike: trip,
      },
    });
  };

  const handleTripCardClick = (
    event: React.MouseEvent<HTMLDivElement>,
    trip: any,
  ) => {
    const target = event.target as HTMLElement;

    /*
     * Keep the existing Book button and any other interactive elements
     * inside HikeTripCard working normally.
     */
    if (
      target.closest(
        'button, a, input, select, textarea, [role="button"], [data-no-card-navigation="true"]',
      )
    ) {
      return;
    }

    openHikeDetails(trip);
  };

  const handleTripCardKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    trip: any,
  ) => {
    if (event.target !== event.currentTarget) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openHikeDetails(trip);
    }
  };

  return (
    <Box color={pageText} px={'15px'} pb={'15px'}>
      {/* Search Form */}
      <Box
        mt={2}
        borderRadius={cardsBorderRadii}
        bg={shellBg}
        boxShadow={panelShadow}
        p={{ base: 3, md: 4 }}
        backdropFilter={isDark ? "blur(8px)" : undefined}
      >
        <VStack align="stretch" spacing={0}>
          <Flex
            gap={4}
            direction={{ base: "column", md: "row" }}
            flexWrap="wrap"
            alignItems={{ base: "stretch", md: "flex-end" }}
          >
            <Box flex="1">
              <Flex
                mb={2}
                color={mutedText}
                alignItems="center"
                gap={2}
                fontSize="11px"
                fontWeight="600"
                letterSpacing="0.02em"
              >
                <MapPin size={11} color={brand.electricBlue} />
                <label>Origin</label>
              </Flex>

              {mapsReady ? (
                <AutocompleteInput
                  placeholder="From Address"
                  value={from}
                  onPlaceSelected={setFrom}
                />
              ) : (
                <Input
                  placeholder="From Address"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                  {...inputStyles}
                />
              )}
            </Box>

            <Box flex="1">
              <Flex
                mb={2}
                color={mutedText}
                alignItems="center"
                gap={2}
                fontSize="11px"
                fontWeight="600"
                letterSpacing="0.02em"
              >
                <MapPin size={11} color={brand.electricBlue} />
                <label>Destination</label>
              </Flex>

              {mapsReady ? (
                <AutocompleteInput
                  placeholder="To Address"
                  value={to}
                  onPlaceSelected={setTo}
                />
              ) : (
                <Input
                  placeholder="To Address"
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                  {...inputStyles}
                />
              )}
            </Box>

            <Flex gap={4} alignItems="flex-end" flexWrap="wrap">
              <Box
                flex="1"
                mt={{ base: "4px", md: "0" }}
                minW={{ base: "100%", md: "auto" }}
              >
                <CustomDatePicker
                  placeholder="Departure Date"
                  selected={date}
                  onChange={setDate}
                />
              </Box>
              <Box minW={{ base: "100%", md: "170px" }}>
                <CustomMenu
                  value={mode}
                  onChange={(val) => setMode(val as TravelMode)}
                  options={[...travelModes]}
                />
              </Box>
            </Flex>

            <Box>
              <Button
                onClick={handleSearch}
                isDisabled={loading}
                px="30px"
                minW="110px"
                color="white"
                bgImage={searchButtonBg}
                _hover={{
                  bgImage: searchButtonHover,
                  transform: "translateY(-1px)",
                  boxShadow: theme.shadowMd,
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                borderRadius="5px"
                boxShadow={theme.shadowSm}
                transition="all 0.2s ease"
              >
                Search
              </Button>
            </Box>
          </Flex>
        </VStack>
      </Box>

      {!mapsReady && (
        <Box
          mt={4}
          p={3}
          borderRadius={cardsBorderRadii}
          bg={softBg}
          color={subtleText}
          fontSize="sm"
          boxShadow={theme.shadowSm}
        >
          Map address autocomplete is still loading. You can type addresses
          manually for now.
        </Box>
      )}

      {loading ? (
        <Flex
          mt={8}
          minH="calc(100vh - 260px)"
          w="100%"
          align="center"
          justify="center"
          borderRadius={cardsBorderRadii}
          bg={cardBg}
        >
          <GlobalSpinner />
        </Flex>
      ) : (
        <>
          {/* Day Tabs */}
          <Box mt={8} overflowX="auto" overflowY="hidden">
            <Tabs
              variant="simple"
              overflowX="auto"
              overflowY="hidden"
              index={
                selectedTabDate
                  ? next30Days.findIndex((d) => d.value === selectedTabDate)
                  : -1
              }
            >
              <TabList
                gap={2}
                bg={softBg}
                border={`1px solid ${theme.border}`}
                mb={4}
                overflowX="auto"
                overflowY="hidden"
              >
                {next30Days.map((day) => (
                  <Tab
                    key={day.value}
                    ref={(el) => {
                      tabRefs.current[day.value] = el;
                    }}
                    onClick={() => handleTabClick(day.value)}
                    whiteSpace="nowrap"
                    flexShrink={0}
                    fontSize="sm"
                    px={4}
                    py={3}
                    fontWeight="700"
                    color={mutedText}
                    maxW="100px"
                    _hover={{
                      color: pageText,
                    }}
                    _selected={{
                      color: pageText,
                      bg: cardBg,
                      borderTop: "2px solid #6ec5c4",
                      boxShadow: cardShadow,
                    }}
                  >
                    {day.label}
                  </Tab>
                ))}
              </TabList>
            </Tabs>
          </Box>

          {/* Results & Map */}
          <Flex
            mt={4}
            gap={6}
            direction={{ base: "column", md: "row" }}
            align="stretch"
          >
            <VStack
              borderRadius={cardsBorderRadii}
              bg={cardBg}
              w="100%"
              align="stretch"
              minW={0}
            >
              <Stack width="100%" spacing={4} p={0}>
                {error && (
                  <Text
                    color={theme.danger}
                    textAlign="center"
                    fontWeight="600"
                  >
                    {error}
                  </Text>
                )}

                {!loading && !error && filteredTrips.length === 0 && (
                  <VStack py={10} spacing={3} textAlign="center">
                    <Box
                      bg={
                        isDark
                          ? `${brand.electricBlue}18`
                          : `${brand.electricBlue}14`
                      }
                      borderRadius={cardsBorderRadii}
                      p={4}
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      boxShadow={theme.shadowSm}
                    >
                      <InfoOutlineIcon boxSize={8} color={brand.electricBlue} />
                    </Box>
                    <Text fontSize="lg" fontWeight="semibold" color={pageText}>
                      {hasSearched
                        ? "No trips found for your search"
                        : allTrips.length === 0
                          ? "No trips are currently available"
                          : `No trips found for ${selectedTabDate}`}
                    </Text>
                    <Text fontSize="sm" color={subtleText} maxW="80%">
                      {allTrips.length === 0 && !hasSearched
                        ? "The trips API returned no trip records. Confirm that the deployed app is connected to the production database."
                        : "Try another date, adjust your filters, or check back later for new trips."}
                    </Text>
                  </VStack>
                )}

                {filteredTrips.map((trip: any) => {
                  const tripId = trip._id || trip.id;

                  return (
                    <Box
                      key={tripId}
                      role="link"
                      tabIndex={0}
                      cursor="pointer"
                      borderRadius={cardsBorderRadii}
                      transition="transform 0.2s ease, box-shadow 0.2s ease"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: theme.shadowMd,
                      }}
                      _focusVisible={{
                        outline: `2px solid ${brand.electricBlue}`,
                        outlineOffset: "3px",
                      }}
                      onClick={(event) => handleTripCardClick(event, trip)}
                      onKeyDown={(event) => handleTripCardKeyDown(event, trip)}
                      aria-label={`View trip details from ${
                        trip.from || "origin"
                      } to ${trip.to || "destination"}`}
                    >
                      <HikeTripCard
                        trip={{
                          id: tripId,
                          from: trip.from,
                          to: trip.to,
                          date: trip.departDate || trip.date,
                          seats:
                            trip.seatsAvailable ||
                            trip.availableSeats ||
                            trip.seats ||
                            0,
                          driverId: trip.driverId || trip.userId,
                          carType: trip.carType || "Unknown",
                          price: trip.priceMk || trip.price || 0,
                          departTime: trip.departTime,
                          arriveTime: trip.arriveTime,
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </VStack>

            {/* Map */}
            {hasSearched && mapsReady && directions && (
              <Box
                borderRadius={cardsBorderRadii}
                bg={cardBg}
                boxShadow={cardShadow}
                maxW={{ base: "100%", md: "390px" }}
                w="100%"
                h="fit-content"
                overflow="hidden"
              >
                <Box p={{ base: 4, md: 5 }}>
                  {distance && (
                    <VStack
                      align="start"
                      mb={4}
                      spacing={2}
                      p={4}
                      borderRadius={cardsBorderRadii}
                      bg={softBg}
                      boxShadow={theme.shadowSm}
                    >
                      <Text color={pageText}>
                        <strong>Distance:</strong> {distance}
                      </Text>
                      <Text color={pageText}>
                        <strong>Duration:</strong> {duration}
                      </Text>
                      {cost !== null && (
                        <Text color={pageText}>
                          <strong>Estimated Cost:</strong> MWK {cost}
                        </Text>
                      )}
                    </VStack>
                  )}
                  {directions && (
                    <MapDisplay directions={directions} center={mapCenter} />
                  )}
                </Box>
              </Box>
            )}
          </Flex>
        </>
      )}
    </Box>
  );
};
